type OnsiteSubscriptionListener = () => void;
type OnsiteSubscriptionEmailListener = (email: string) => void;

const onsiteSubscriptionListeners = new Set<OnsiteSubscriptionListener>();
const onsiteSubscriptionEmailListeners = new Set<OnsiteSubscriptionEmailListener>();
let onsiteSubscriptionInterceptorInstalled = false;

const resolveRequestUrl = (input: RequestInfo | URL): string => {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  if (input instanceof Request) return input.url;

  return String(input);
};

const isKlaviyoOnsiteSubscriptionUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url, window.location.href);

    return (
      parsed.hostname === 'a.klaviyo.com' &&
      parsed.pathname.replace(/\/$/, '') === '/client/subscriptions' &&
      parsed.searchParams.get('onsite') === 'true'
    );
  } catch {
    return false;
  }
};

const notifyOnsiteSubscriptionListeners = () => {
  onsiteSubscriptionListeners.forEach((listener) => listener());
};

const extractEmailFromKlaviyoSubscriptionBody = (body: unknown): string | null => {
  if (body == null) return null;

  try {
    const parsed =
      typeof body === 'string'
        ? (JSON.parse(body) as {
            data?: { attributes?: { profile?: { data?: { attributes?: { email?: string } } } } };
          })
        : (body as {
            data?: { attributes?: { profile?: { data?: { attributes?: { email?: string } } } } };
          });

    const email = parsed.data?.attributes?.profile?.data?.attributes?.email;
    return typeof email === 'string' ? email : null;
  } catch {
    return null;
  }
};

const notifyOnsiteSubscriptionEmailListeners = (body: unknown) => {
  const email = extractEmailFromKlaviyoSubscriptionBody(body);
  if (!email) return;

  onsiteSubscriptionEmailListeners.forEach((listener) => listener(email));
};

const resolveRequestBody = (input: RequestInfo | URL, init?: RequestInit): unknown => {
  if (init?.body != null) return init.body;

  if (input instanceof Request && input.body != null) {
    return input;
  }

  return null;
};

const installOnsiteSubscriptionInterceptor = () => {
  if (onsiteSubscriptionInterceptorInstalled) return;

  onsiteSubscriptionInterceptorInstalled = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init) => {
    const url = resolveRequestUrl(input);

    if (isKlaviyoOnsiteSubscriptionUrl(url)) {
      const body = resolveRequestBody(input, init);

      if (body instanceof Request) {
        void body
          .clone()
          .text()
          .then((text) => notifyOnsiteSubscriptionEmailListeners(text));
      } else {
        notifyOnsiteSubscriptionEmailListeners(body);
      }
    }

    const response = await originalFetch(input, init);

    if (isKlaviyoOnsiteSubscriptionUrl(url) && response.status === 202) {
      notifyOnsiteSubscriptionListeners();
    }

    return response;
  };

  const xhrUrls = new WeakMap<XMLHttpRequest, string>();
  const originalOpen = XMLHttpRequest.prototype.open as (
    this: XMLHttpRequest,
    method: string,
    url: string | URL,
    async?: boolean,
    username?: string | null,
    password?: string | null,
  ) => void;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (
    method: string,
    url: string | URL,
    async = true,
    username?: string | null,
    password?: string | null,
  ) {
    xhrUrls.set(this, typeof url === 'string' ? url : url.toString());

    return Reflect.apply(originalOpen, this, [method, url, async, username, password]);
  };

  XMLHttpRequest.prototype.send = function (...args) {
    const url = xhrUrls.get(this) ?? '';

    if (isKlaviyoOnsiteSubscriptionUrl(url)) {
      notifyOnsiteSubscriptionEmailListeners(args[0]);
    }

    this.addEventListener('load', () => {
      if (isKlaviyoOnsiteSubscriptionUrl(url) && this.status === 202) {
        notifyOnsiteSubscriptionListeners();
      }
    });

    return originalSend.apply(this, args);
  };
};

export const watchKlaviyoOnsiteSubscription = (
  listener: OnsiteSubscriptionListener,
): (() => void) => {
  installOnsiteSubscriptionInterceptor();
  onsiteSubscriptionListeners.add(listener);

  return () => onsiteSubscriptionListeners.delete(listener);
};

export const watchKlaviyoOnsiteSubscriptionEmail = (
  listener: OnsiteSubscriptionEmailListener,
): (() => void) => {
  installOnsiteSubscriptionInterceptor();
  onsiteSubscriptionEmailListeners.add(listener);

  return () => onsiteSubscriptionEmailListeners.delete(listener);
};
